import {
  ensureOptionPresent,
  findOptionByRawValue,
  resolveFirstPositiveOptionId,
  toPositiveOptionId,
} from '@/utils/optionValues';
import { toBooleanFlag } from '@/utils/dataNormalization';

export const normalizeExpenseFeeFrequencyValue = (rawFrequency) => {
  const value = String(rawFrequency || '').trim().toUpperCase();
  if (value === '') {
    return '';
  }

  if (value === 'ONE_PAYMENT' || value === 'UNIQUE') {
    return 'unique';
  }

  if (value === 'MONTHLY_PAYMENTS' || value === 'MONTHLY') {
    return 'monthly';
  }

  if (value === 'MULTIPLE_PAYMENTS' || value === 'MULTIPLE') {
    return 'multiple';
  }

  return '';
};

export const normalizeExpenseFeeFirstInstallmentValue = (rawValue) => {
  const value = String(rawValue || '').trim();
  if (value === '') {
    return '';
  }

  if (/^\d{4}-\d{2}$/.test(value)) {
    return value;
  }

  const match = /^(\d{4}-\d{2})-\d{2}$/.exec(value);
  if (match) {
    return match[1];
  }

  return '';
};

const resolveFallbackLabel = (resolver, id, defaultPrefix) => {
  if (typeof resolver === 'function') {
    const resolved = String(resolver({ id }) || '').trim();
    if (resolved !== '') {
      return resolved;
    }
  }

  if (typeof resolver === 'string' && resolver.trim() !== '') {
    return resolver.trim();
  }

  return `${defaultPrefix} #${id}`;
};

export const applyExpenseFeeTaxSelectionFromDetail = ({
  data = {},
  form = {},
  options = {},
  taxFallbackLabel = null,
  taxGroupFallbackLabel = null,
} = {}) => {
  const taxId = resolveFirstPositiveOptionId(
    data.tax_id,
    data.tax?.id,
    data.taxes?.[0]?.id,
    data.taxes?.[0]?.value,
    data.taxes?.[0],
    data.tax_ids?.[0],
  );
  const taxGroupId = resolveFirstPositiveOptionId(
    data.tax_group_id,
    data.tax_group?.id,
  );

  let nextOptions = options && typeof options === 'object' ? options : {};

  if (taxId > 0) {
    form.withoutTaxes = false;
    form.taxType = 'individual';
    form.taxGroup = null;

    const selectedTax = findOptionByRawValue(nextOptions.taxes, [taxId, data.tax, data.taxes])
      || {
        value: taxId,
        label: resolveFallbackLabel(taxFallbackLabel, taxId, 'Impuesto'),
      };

    const taxResolution = ensureOptionPresent(nextOptions, 'taxes', selectedTax);
    nextOptions = taxResolution.options;
    const safeTax = taxResolution.option;
    form.taxes = safeTax ? [safeTax] : [];
    return nextOptions;
  }

  if (taxGroupId > 0) {
    form.withoutTaxes = false;
    form.taxType = 'group';
    form.taxes = [];

    const selectedGroup = findOptionByRawValue(nextOptions.taxGroups, [taxGroupId, data.tax_group])
      || {
        value: taxGroupId,
        label: resolveFallbackLabel(taxGroupFallbackLabel, taxGroupId, 'Grupo'),
      };

    const groupResolution = ensureOptionPresent(nextOptions, 'taxGroups', selectedGroup);
    nextOptions = groupResolution.options;
    form.taxGroup = groupResolution.option;
    return nextOptions;
  }

  form.withoutTaxes = true;
  form.taxType = 'individual';
  form.taxes = [];
  form.taxGroup = null;
  return nextOptions;
};

const resolveSaleFallbackLabel = (resolver, id) => {
  if (typeof resolver === 'function') {
    const resolved = String(resolver({ id }) || '').trim();
    if (resolved !== '') {
      return resolved;
    }
  }

  if (typeof resolver === 'string' && resolver.trim() !== '') {
    return resolver.trim();
  }

  return `Venta #${id}`;
};

export const applyExpenseFeeLockedSaleContext = ({
  form = {},
  options = {},
  lockedSaleId = 0,
  fallbackSaleLabel = null,
} = {}) => {
  const safeLockedSaleId = Number(lockedSaleId || 0);
  if (safeLockedSaleId <= 0) {
    return;
  }

  const salesOptions = Array.isArray(options?.sales) ? options.sales : [];
  const matchedSale = salesOptions.find((option) => toPositiveOptionId(option) === safeLockedSaleId);

  form.sale = matchedSale || {
    value: safeLockedSaleId,
    label: resolveSaleFallbackLabel(fallbackSaleLabel, safeLockedSaleId),
  };

  const saleCustomerId = Number(
    matchedSale?.meta?.company_id
    || matchedSale?.meta?.customer_id
    || matchedSale?.meta?.company?.id
    || matchedSale?.meta?.customer?.id
    || matchedSale?.company_id
    || matchedSale?.customer_id
    || 0
  ) || 0;

  if (saleCustomerId <= 0) {
    return;
  }

  const customerOptions = Array.isArray(options?.customers) ? options.customers : [];
  const matchedCustomer = customerOptions.find((option) => toPositiveOptionId(option) === saleCustomerId);
  if (matchedCustomer) {
    form.customer = matchedCustomer;
  }
};

export const enforceExpenseFeeAmountConstraints = ({
  form = {},
  warnings = {},
  warningLocks = {},
  sourceField = '',
  warningMessages = {},
} = {}) => {
  const amount = Number(form?.amount || 0);
  const discount = Number(form?.discount || 0);
  const deductions = Number(form?.deductions || 0);

  if (discount > amount) {
    form.discount = 0;
    warnings.discount = warningMessages?.discountReset || '';
    warningLocks.discount = true;
  } else if ((sourceField === 'discount' || sourceField === 'amount') && !warningLocks.discount) {
    warnings.discount = '';
  } else if (sourceField === 'discount' && warningLocks.discount && discount <= amount) {
    warningLocks.discount = false;
  }

  if (deductions > amount) {
    form.deductions = 0;
    warnings.deductions = warningMessages?.deductionsReset || '';
    warningLocks.deductions = true;
  } else if ((sourceField === 'deductions' || sourceField === 'amount') && !warningLocks.deductions) {
    warnings.deductions = '';
  } else if (sourceField === 'deductions' && warningLocks.deductions && deductions <= amount) {
    warningLocks.deductions = false;
  }

  if (sourceField === 'amount' && discount <= amount) {
    warningLocks.discount = false;
  }

  if (sourceField === 'amount' && deductions <= amount) {
    warningLocks.deductions = false;
  }
};

export const resolveExpenseFeeFirstInvalidField = ({
  validation = {},
  orderedFields = [],
} = {}) => {
  for (let index = 0; index < orderedFields.length; index += 1) {
    const field = orderedFields[index];
    if (validation?.[field]?.$error) {
      return field;
    }
  }

  return '';
};

export const scrollExpenseFeeElementIntoView = (target) => {
  if (!target || typeof target.scrollIntoView !== 'function') {
    return;
  }

  try {
    target.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
  } catch (error) {
    target.scrollIntoView();
  }
};

export const focusExpenseFeeFirstInvalidField = ({
  vm = null,
  invalidField = '',
  selectorMap = {},
} = {}) => {
  if (!vm || invalidField === '' || typeof vm.$nextTick !== 'function') {
    return;
  }

  vm.$nextTick(() => {
    const root = vm.$el;
    if (!root || typeof root.querySelector !== 'function') {
      return;
    }

    const selectors = Array.isArray(selectorMap[invalidField]) ? selectorMap[invalidField] : [];
    for (let index = 0; index < selectors.length; index += 1) {
      const selector = selectors[index];
      const element = root.querySelector(selector);
      if (!element) {
        continue;
      }

      const scrollTarget = element.matches('input, textarea')
        ? element
        : (element.querySelector('input, textarea') || element);

      scrollExpenseFeeElementIntoView(scrollTarget);
      return;
    }
  });
};

export const resolveExpenseFeePaidState = ({ data = {} } = {}) => {
  const source = data && typeof data === 'object' ? data : {};

  const explicitStatus = String(source.status || source.expense_status || '').trim().toLowerCase();
  if (explicitStatus !== '') {
    if (
      explicitStatus.includes('no pagad')
      || explicitStatus.includes('unpaid')
      || explicitStatus.includes('sin pag')
      || explicitStatus.includes('pendient')
    ) {
      return false;
    }

    if (explicitStatus.includes('pagad') || explicitStatus.includes('paid')) {
      return true;
    }
  }

  const paymentId = Number(
    source.payment_id
    || source.payment?.id
    || source.paymentId
    || 0
  ) || 0;
  if (paymentId > 0) {
    return true;
  }

  if (toBooleanFlag(source.is_paid) || toBooleanFlag(source.paid) || toBooleanFlag(source.has_payment)) {
    return true;
  }

  const statusCandidates = [
    source.is_paid,
    source.paid,
    source.has_payment,
    source.expense_status,
    source.status,
    source.status_code,
    source.payment_type_code,
    source.payment_type_name,
  ];

  for (let index = 0; index < statusCandidates.length; index += 1) {
    const normalized = String(statusCandidates[index] || '').trim().toLowerCase();
    if (normalized === '') {
      continue;
    }

    if (
      normalized.includes('no pagad')
      || normalized.includes('unpaid')
      || normalized.includes('sin pag')
      || normalized.includes('pendient')
    ) {
      continue;
    }

    if (['paid', 'pagado', 'payed', 'liquidado', 'liquidada'].includes(normalized)) {
      return true;
    }

    if (normalized.includes('pagad') || normalized.includes('paid')) {
      return true;
    }
  }

  return false;
};

export const resolveExpenseFeeStatusLabel = ({
  data = {},
  statusLabels = {},
} = {}) => {
  const source = data && typeof data === 'object' ? data : {};

  const notApprovedLabel = String(statusLabels?.notApproved || '').trim();
  const pendingLabel = String(statusLabels?.pending || '').trim();
  const paidLabel = String(statusLabels?.paid || '').trim();
  const canceledLabel = String(statusLabels?.canceled || '').trim();

  const explicitStatus = String(source.status || source.expense_status || '').trim();
  if (explicitStatus !== '') {
    return explicitStatus;
  }

  const paymentTypeCode = String(source.payment_type_code || source.status_code || '').trim().toUpperCase();
  const paymentTypeName = String(source.payment_type_name || '').trim();
  const normalizedPaymentTypeName = paymentTypeName.toLowerCase();
  const paymentId = Number(source.payment_id || source.payment?.id || source.paymentId || 0) || 0;
  const isApproved = Number(source.is_approved) === 0 ? 0 : 1;

  if (paymentTypeCode === 'PENDING') {
    return isApproved === 0 ? notApprovedLabel : pendingLabel;
  }

  if (paymentTypeCode === 'DONE') {
    return paidLabel;
  }

  if (paymentTypeCode === 'CANCELED') {
    return canceledLabel;
  }

  if (['pronostico', 'pronóstico', 'pending', 'forecast'].includes(normalizedPaymentTypeName)) {
    return pendingLabel;
  }

  if (paymentTypeName !== '') {
    return paymentTypeName;
  }

  if (paymentId > 0) {
    return paidLabel;
  }

  return isApproved === 0 ? notApprovedLabel : pendingLabel;
};
